// =============================================================================
// FOOTER COMPONENT
// =============================================================================

import React from 'react';
import { Heart } from 'lucide-react';
import { NewsletterSignup } from '@/components/Newsletter/NewsletterSignup';
import type { NavItem } from '@/types';

interface FooterProps {
  brand?: string;
  description?: string;
  links?: NavItem[];
  socialLinks?: NavItem[];
  copyright?: string;
  showNewsletter?: boolean;
  privacyLink?: string;
  termsLink?: string;
}

export const Footer: React.FC<FooterProps> = ({
  brand = 'Alex Finger',
  links = [],
  socialLinks = [],
  copyright = `© ${new Date().getFullYear()} Alex Finger Consulting. All rights reserved.`,
  showNewsletter = true,
}) => {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          {/* Brand Section - Left Side */}
          <div className="footer-brand">
            <img 
              src="/images/logo.png" 
              alt={`${brand} logo`}
              className="footer-logo"
              width="48"
              height="48"
            />
            <h3>{brand}</h3>
          </div>

          {/* Links and Social Section - Right Side */}
          <div className="footer-right-group">
            {/* Links Column */}
            <div className="footer-links">
              <h4>Links</h4>
              {links.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Newsletter Section - Full Width Below */}
            {showNewsletter && (
              <div className="footer-newsletter-section">
                <NewsletterSignup
                  variant="footer"
                  title="Stay Updated"
                  description="Get the latest insights and updates delivered to your inbox."
                  placeholder="Enter your email"
                  buttonText="Subscribe"
                  showIcon={false}
                />
              </div>
            )}

            {/* Social Column */}
            {socialLinks.length > 0 && (
              <div className="footer-social">
                <h4>Connect</h4>
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="footer-bottom">
          <p>
            Made with &nbsp;<Heart size={16} style={{ color: 'var(--color-error)' }} />&nbsp; by{' '}
            <a href="https://www.linkedin.com/in/alex-finger-71117336a/" target="_blank" rel="noopener noreferrer">
             Alex Finger 
            </a> | {copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};
